import { Billing } from '@/data/types';

/** Reserved Service Prices device type carrying the disposal fee (client #54). */
export const DISPOSAL_TYPE = 'disposal';

/**
 * Service Prices device type for the disposal fee of one specific device type.
 *
 * The client charges a different fee per device — 2kg powder is CHF 7.00 where
 * 12kg powder is CHF 23.00 — so one flat rate could not express their price
 * list. A row keyed this way prices that device type's disposal; the plain
 * `disposal` row stays as the fallback for everything without its own.
 */
export const disposalTypeFor = (deviceType: string) =>
  `${DISPOSAL_TYPE}:${deviceType}`;

/**
 * Service Prices device type for the disposal fee of one extinguisher content
 * ("Inhalt"), e.g. `disposal@6 kg Pulver`.
 *
 * The client's disposal list is by size — 2kg powder CHF 7.00, 12kg powder
 * CHF 23.00, 6L foam CHF 15.00 — and their device types carry no size, so a
 * per-type row could not express it. Every extinguisher does carry its
 * content, and on prod those values are exactly the sizes on the list. The
 * original text is kept after the `@` so the row reads back as written; it is
 * matched through {@link normalizeContent} (client #54).
 */
export const disposalContentFor = (content: string) =>
  `${DISPOSAL_TYPE}@${content.trim()}`;

export const isDisposalType = (t: string) =>
  t === DISPOSAL_TYPE ||
  t.startsWith(`${DISPOSAL_TYPE}:`) ||
  t.startsWith(`${DISPOSAL_TYPE}@`);

/** The device type a namespaced disposal row prices, if it names one. */
export const disposalTargetType = (t: string) =>
  t.startsWith(`${DISPOSAL_TYPE}:`)
    ? t.slice(DISPOSAL_TYPE.length + 1)
    : undefined;

/** The content a content-keyed disposal row prices, as originally written. */
export const disposalTargetContent = (t: string) =>
  t.startsWith(`${DISPOSAL_TYPE}@`)
    ? t.slice(DISPOSAL_TYPE.length + 1)
    : undefined;

/**
 * Comparison form of an extinguisher content. The register was typed by hand
 * over years, so the same size turns up as "2 kg Pulver" and "2kg Pulver", or
 * "5 kg Co2" and "5 kg CO₂": case, spacing and the subscript two are ignored.
 */
export const normalizeContent = (s: string) =>
  s.toLowerCase().replace(/₂/g, '2').replace(/\s+/g, '');

/**
 * The distinct contents in a register, one per {@link normalizeContent} form,
 * most-used first. Each is spelled the way most devices spell it, so "2kg
 * Pulver" on one device does not crowd out "2 kg Pulver" on hundreds.
 */
export function rankContents(
  values: Iterable<string | undefined>
): { content: string; count: number }[] {
  const groups = new Map<
    string,
    { count: number; spellings: Map<string, number> }
  >();
  for (const value of values) {
    const raw = value?.trim();
    const key = raw ? normalizeContent(raw) : '';
    if (!raw || !key) continue;
    const g = groups.get(key) ?? { count: 0, spellings: new Map() };
    g.count += 1;
    g.spellings.set(raw, (g.spellings.get(raw) ?? 0) + 1);
    groups.set(key, g);
  }
  return [...groups.values()]
    .map(g => ({
      content: [...g.spellings.entries()].sort(
        ([a, n], [b, m]) => m - n || a.localeCompare(b)
      )[0][0],
      count: g.count,
    }))
    .sort((a, b) => b.count - a.count || a.content.localeCompare(b.content));
}

export interface InvoiceLine {
  kind: 'service' | 'new' | 'disposal';
  label: string;
  articleNr: string;
  qty: number;
  price: number;
  /** Device type a disposal line covers, so the caller can name it. */
  deviceType?: string;
  /** Content ("Inhalt") a disposal line was priced by, as the row names it. */
  content?: string;
}

export interface InvoiceBuild {
  lines: InvoiceLine[];
  warnings: string[];
}

interface BillingDevice {
  id: string;
  billing?: string;
  model?: string;
  /** Device type id, used to price disposal per type (client #54). */
  type?: string;
  /** Content ("Inhalt"), used to price disposal by size (client #54). */
  content?: string;
}

interface PriceRow {
  articleNr: string;
  price: number;
}

interface DisposalContentRow extends PriceRow {
  /** The content as the price row writes it, to name the invoice line. */
  label?: string;
}

/**
 * Does `qty` fall inside a service-price quantity band?
 *
 * The client writes these by hand in the Service Prices screen, so accept the
 * shapes they actually use: "1", "2-5", "6+", "10-", "-4". A blank or
 * unparseable band matches everything, which keeps every existing row working
 * (they were being ignored entirely before — client #55).
 */
export function qtyInRange(range: string | undefined, qty: number): boolean {
  const s = (range ?? '').trim();
  if (!s) return true;
  const openEnded = s.match(/^(\d+)\s*[+-]$/);
  if (openEnded) return qty >= Number(openEnded[1]);
  const upToOnly = s.match(/^[-–]\s*(\d+)$/);
  if (upToOnly) return qty <= Number(upToOnly[1]);
  const band = s.match(/^(\d+)\s*[-–]\s*(\d+)$/);
  if (band) return qty >= Number(band[1]) && qty <= Number(band[2]);
  const single = s.match(/^(\d+)$/);
  if (single) return qty === Number(single[1]);
  return true;
}

interface ServicePriceRow {
  deviceType?: string;
  qtyRange?: string;
}

/**
 * The price row to charge for `deviceType` when `qty` units are being
 * serviced. A row with an explicit band wins over a catch-all row, so a
 * catalogue that mixes "2-5" tiers with an untiered fallback behaves sanely.
 */
export function pickServicePrice<T extends ServicePriceRow>(
  rows: T[],
  deviceType: string,
  qty: number
): T | undefined {
  const forType = rows.filter(r => r.deviceType === deviceType);
  const banded = forType.filter(r => (r.qtyRange ?? '').trim() !== '');
  return (
    banded.find(r => qtyInRange(r.qtyRange, qty)) ??
    forType.find(r => (r.qtyRange ?? '').trim() === '')
  );
}

/** Build invoice lines for the devices serviced on a visit, honouring the
 *  per-device billing type:
 *   - NONE     → not charged (excluded)
 *   - MAINTENANCE (default) → counted into the service line
 *   - NEW      → a "Total Neu <model>" line
 *   - DISPOSED → a disposal line
 *  A missing service price surfaces a warning instead of a silent CHF 0. */
export function buildInvoiceLines(args: {
  devices: BillingDevice[];
  servicedIds: string[];
  servicePrices: PriceRow[];
  serviceArticleNr: string;
  /** Catalogue price per model name, for devices billed as NEW (client #60). */
  modelPrices?: Record<string, number>;
  /** Fallback price for taking one device away (client #54). */
  disposalPrice?: number;
  disposalArticleNr?: string;
  /** Per-device-type disposal fees, keyed by device type id (client #54). */
  disposalByType?: Record<string, { articleNr: string; price: number }>;
  /**
   * Per-content disposal fees, keyed by content (client #54). Keys are
   * compared through {@link normalizeContent}, so raw or normalised both work.
   */
  disposalByContent?: Record<string, DisposalContentRow>;
}): InvoiceBuild {
  const serviced = new Set(args.servicedIds);
  const charged = args.devices.filter(
    d =>
      serviced.has(d.id) && (d.billing ?? Billing.MAINTENANCE) !== Billing.NONE
  );

  const lines: InvoiceLine[] = [];
  const warnings: string[] = [];

  const maintenance = charged.filter(
    d => (d.billing ?? Billing.MAINTENANCE) === Billing.MAINTENANCE
  );
  if (maintenance.length) {
    const pr = args.servicePrices.find(
      p => p.articleNr === args.serviceArticleNr
    );
    if (!pr) warnings.push('missing service price: ' + args.serviceArticleNr);
    lines.push({
      kind: 'service',
      label: 'Service / Kontrolle',
      articleNr: args.serviceArticleNr,
      qty: maintenance.length,
      price: pr?.price ?? 0,
    });
  }

  const news = charged.filter(d => d.billing === Billing.NEW);
  const byModel: Record<string, number> = {};
  news.forEach(d => {
    const m = d.model ?? '—';
    byModel[m] = (byModel[m] ?? 0) + 1;
  });
  Object.entries(byModel).forEach(([model, qty]) => {
    const price = args.modelPrices?.[model];
    // A new device with no catalogue price used to bill silently at 0; say so
    // instead, the way a missing service price already does.
    if (price == null) warnings.push('missing model price: ' + model);
    lines.push({
      kind: 'new',
      label: 'Total Neu ' + model,
      articleNr: '—',
      qty,
      price: price ?? 0,
    });
  });

  // The fee differs per device (client #54), so each disposed device is priced
  // by the most specific row that covers it: its content ("6 kg Pulver") →
  // its device type → the generic disposal row. One line per pricing row, so
  // a mixed job does not print the same price several times over; everything
  // left to the generic row collapses into a single line.
  const disposed = charged.filter(d => d.billing === Billing.DISPOSED);
  if (disposed.length) {
    const byContent = new Map<string, DisposalContentRow>();
    Object.entries(args.disposalByContent ?? {}).forEach(([key, row]) => {
      const k = normalizeContent(key);
      if (k && !byContent.has(k)) byContent.set(k, row);
    });
    type Group = {
      row?: PriceRow;
      deviceType?: string;
      content?: string;
      devices: BillingDevice[];
    };
    const groups = new Map<string, Group>();
    const add = (
      key: string,
      d: BillingDevice,
      make: () => Omit<Group, 'devices'>
    ) => {
      const group = groups.get(key);
      if (group) group.devices.push(d);
      else groups.set(key, { ...make(), devices: [d] });
    };
    disposed.forEach(d => {
      const c = d.content ? normalizeContent(d.content) : '';
      const contentRow = c ? byContent.get(c) : undefined;
      if (contentRow) {
        add(`c:${c}`, d, () => ({
          row: contentRow,
          content: contentRow.label?.trim() || d.content?.trim(),
        }));
        return;
      }
      const typeRow = d.type ? args.disposalByType?.[d.type] : undefined;
      if (d.type && typeRow) {
        const type = d.type;
        add(`t:${type}`, d, () => ({ row: typeRow, deviceType: type }));
        return;
      }
      add('', d, () => ({}));
    });
    [...groups.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([, g]) => {
        const price = g.row ? g.row.price : args.disposalPrice;
        if (price == null) warnings.push('missing disposal price');
        const named = g.content ?? g.deviceType;
        lines.push({
          kind: 'disposal',
          label: named ? `Entsorgung · ${named}` : 'Entsorgung / Disposal',
          articleNr: g.row?.articleNr ?? args.disposalArticleNr ?? '—',
          qty: g.devices.length,
          price: price ?? 0,
          deviceType: g.deviceType,
          content: g.content,
        });
      });
  }

  return { lines, warnings };
}
